// ---------------------------------------------------------------------------
// Prop Batching — BatchedMesh-based draw-call reduction for static props.
//
// Groups props by material, creates one THREE.BatchedMesh per material group.
// Unlike InstancedMesh, BatchedMesh supports different geometries in the same
// draw call, making it ideal for heterogeneous prop collections that share a
// material (e.g. several wooden props with the same wood material).
//
// Phase 10 Optimization — target: 100-200 draw calls/frame
// ---------------------------------------------------------------------------

import * as THREE from 'three';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface BatchEntry {
  geometryId: number;
  instanceId: number;
  propId: string;
  bounds: THREE.Box3;
}

export interface BatchableProp {
  mesh: THREE.Mesh;
  propType: string;
  position: THREE.Vector3;
  rotation?: THREE.Euler;
  scale?: THREE.Vector3;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Flatten a Group into a single merged BufferGeometry + pick the dominant
 * material. Props are often THREE.Group masquerading as Mesh (see props.ts).
 */
function flattenToMesh(obj: THREE.Object3D): THREE.Mesh | null {
  if (obj instanceof THREE.Mesh && !(obj as any).isGroup) {
    return obj;
  }

  // Collect all child meshes
  const meshes: THREE.Mesh[] = [];
  obj.traverse((child: THREE.Object3D) => {
    if (child instanceof THREE.Mesh && child.geometry) {
      meshes.push(child);
    }
  });

  if (meshes.length === 0) return null;
  if (meshes.length === 1) return meshes[0];

  // Merge child geometries into one
  const geometries: THREE.BufferGeometry[] = [];
  let totalVertices = 0;
  let totalIndices = 0;

  for (const m of meshes) {
    const geo = m.geometry.clone();
    m.updateMatrix();
    geo.applyMatrix4(m.matrix);
    geometries.push(geo);
    totalVertices += geo.attributes.position.count;
    totalIndices += geo.index ? geo.index.count : geo.attributes.position.count;
  }

  const positions = new Float32Array(totalVertices * 3);
  const normals = new Float32Array(totalVertices * 3);
  const indices = new Uint32Array(totalIndices);
  let vOff = 0;
  let iOff = 0;

  for (const geo of geometries) {
    const posAttr = geo.attributes.position;
    const normAttr = geo.attributes.normal;

    for (let i = 0; i < posAttr.count; i++) {
      positions[(vOff + i) * 3] = posAttr.getX(i);
      positions[(vOff + i) * 3 + 1] = posAttr.getY(i);
      positions[(vOff + i) * 3 + 2] = posAttr.getZ(i);
      if (normAttr) {
        normals[(vOff + i) * 3] = normAttr.getX(i);
        normals[(vOff + i) * 3 + 1] = normAttr.getY(i);
        normals[(vOff + i) * 3 + 2] = normAttr.getZ(i);
      }
    }

    if (geo.index) {
      for (let i = 0; i < geo.index.count; i++) {
        indices[iOff + i] = geo.index.getX(i) + vOff;
      }
      iOff += geo.index.count;
    } else {
      for (let i = 0; i < posAttr.count; i++) {
        indices[iOff + i] = vOff + i;
      }
      iOff += posAttr.count;
    }

    vOff += posAttr.count;
    geo.dispose();
  }

  const merged = new THREE.BufferGeometry();
  merged.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  merged.setAttribute('normal', new THREE.BufferAttribute(normals, 3));
  merged.setIndex(new THREE.BufferAttribute(indices, 1));

  const result = new THREE.Mesh(merged, meshes[0].material);
  result.castShadow = true;
  result.receiveShadow = true;
  return result;
}

// ---------------------------------------------------------------------------
// PropBatcher
// ---------------------------------------------------------------------------

export class PropBatcher {
  private batches: Map<string, THREE.BatchedMesh> = new Map();
  private entries: BatchEntry[] = [];
  /** Props that were too few to batch — left as individual meshes. */
  private unbatched: THREE.Mesh[] = [];

  /**
   * Group props by material, create one BatchedMesh per material group.
   * Each BatchedMesh can hold different geometries (unlike InstancedMesh).
   *
   * @param props - Array of props to batch
   * @param scene - Scene to add BatchedMeshes to
   * @returns Number of draw calls saved
   */
  batchProps(props: BatchableProp[], scene: THREE.Scene): number {
    if (props.length === 0) return 0;

    // Dispose previous batches to prevent geometry accumulation on re-batch
    if (this.batches.size > 0) {
      this.dispose();
    }

    // Flatten groups into single meshes for batching
    const flatProps: Array<{
      mesh: THREE.Mesh;
      propType: string;
      position: THREE.Vector3;
      rotation?: THREE.Euler;
      scale?: THREE.Vector3;
    }> = [];

    for (const prop of props) {
      const flat = flattenToMesh(prop.mesh);
      if (flat) {
        flatProps.push({
          mesh: flat,
          propType: prop.propType,
          position: prop.position,
          rotation: prop.rotation,
          scale: prop.scale,
        });
      }
    }

    // Group by material key
    const groups = new Map<string, typeof flatProps>();
    for (const prop of flatProps) {
      const matKey = this.getMaterialKey(prop.mesh.material as THREE.Material);
      if (!groups.has(matKey)) groups.set(matKey, []);
      groups.get(matKey)!.push(prop);
    }

    let drawCallsSaved = 0;

    for (const [matKey, group] of groups) {
      // Not worth batching fewer than 3 props
      if (group.length < 3) {
        for (const prop of group) {
          this.unbatched.push(prop.mesh);
          if (prop.mesh.parent === null) scene.add(prop.mesh);
        }
        continue;
      }

      // Collect unique geometries by prop type
      const uniqueGeometries = new Map<string, THREE.BufferGeometry>();
      for (const prop of group) {
        if (!uniqueGeometries.has(prop.propType)) {
          uniqueGeometries.set(prop.propType, prop.mesh.geometry);
        }
      }

      // Calculate max vertex/index counts needed
      let maxVertexCount = 0;
      let maxIndexCount = 0;
      for (const geo of uniqueGeometries.values()) {
        maxVertexCount += geo.attributes.position.count;
        maxIndexCount += geo.index?.count || geo.attributes.position.count;
      }

      // Safety margin: multiply by number of instances per geometry
      const instanceCounts = new Map<string, number>();
      for (const prop of group) {
        instanceCounts.set(
          prop.propType,
          (instanceCounts.get(prop.propType) || 0) + 1,
        );
      }

      // BatchedMesh vertex/index budget = sum of all geometries that will be added
      // (BatchedMesh stores each geometry once and references it per instance)
      try {
        const batch = new THREE.BatchedMesh(
          group.length,
          maxVertexCount,
          maxIndexCount,
          group[0].mesh.material as THREE.Material,
        );
        batch.sortObjects = false; // We handle our own sort via PVS
        batch.perObjectFrustumCulled = true;
        batch.userData.isBatched = true;
        batch.userData.cp_collision = false;

        // Add each unique geometry
        const geoIds = new Map<string, number>();
        for (const [geoKey, geo] of uniqueGeometries) {
          geoIds.set(geoKey, batch.addGeometry(geo));
        }

        // Add instances
        for (const prop of group) {
          const geoId = geoIds.get(prop.propType)!;
          const instanceId = batch.addInstance(geoId);

          const matrix = new THREE.Matrix4();
          matrix.compose(
            prop.position,
            new THREE.Quaternion().setFromEuler(
              prop.rotation || new THREE.Euler(),
            ),
            prop.scale || new THREE.Vector3(1, 1, 1),
          );
          batch.setMatrixAt(instanceId, matrix);

          // Compute bounds for this instance
          const bounds = new THREE.Box3();
          const tempGeo = prop.mesh.geometry.clone();
          tempGeo.applyMatrix4(matrix);
          tempGeo.computeBoundingBox();
          if (tempGeo.boundingBox) {
            bounds.copy(tempGeo.boundingBox);
          }
          tempGeo.dispose();

          this.entries.push({
            geometryId: geoId,
            instanceId,
            propId: `${prop.propType}_${prop.position.x}_${prop.position.y}_${prop.position.z}`,
            bounds,
          });

          // Remove original mesh from scene
          if (prop.mesh.parent) {
            prop.mesh.parent.remove(prop.mesh);
          }
          // Do NOT dispose prop.mesh.geometry here: the prop LOD system may
          // still hold a reference to this mesh (see prop-lod.ts), and the
          // flatten step above sometimes returns the caller's original mesh
          // rather than a freshly-merged one. GC will release the buffer
          // once the LOD system drops its reference.
        }

        batch.computeBoundingBox();
        batch.computeBoundingSphere();
        batch.castShadow = true;
        batch.receiveShadow = true;
        scene.add(batch);
        this.batches.set(matKey, batch);

        // One draw call for the batch vs N individual draw calls
        drawCallsSaved += group.length - 1;
      } catch (e) {
        // BatchedMesh can fail with incompatible geometries — fall back
        console.warn(`PropBatcher: failed to batch group "${matKey}":`, e);
        for (const prop of group) {
          this.unbatched.push(prop.mesh);
          if (prop.mesh.parent === null) scene.add(prop.mesh);
        }
      }
    }

    return drawCallsSaved;
  }

  /** Per-instance visibility is handled by Three.js perObjectFrustumCulled. */
  updateVisibility(_camera: THREE.Camera): void {
    // BatchedMesh has built-in per-object frustum culling when
    // perObjectFrustumCulled = true (set in batchProps).
    // No manual update needed.
  }

  private getMaterialKey(mat: THREE.Material): string {
    // Key by material type + first 8 chars of UUID for deduplication
    return `${mat.type}_${mat.uuid.slice(0, 8)}`;
  }

  /** Get current number of BatchedMesh draw calls. */
  getDrawCallCount(): number {
    return this.batches.size;
  }

  /** Get total entries tracked. */
  getEntryCount(): number {
    return this.entries.length;
  }

  /** Dispose all batched meshes and free GPU resources. */
  dispose(): void {
    for (const batch of this.batches.values()) {
      batch.geometry.dispose();
      if (Array.isArray(batch.material)) {
        batch.material.forEach((m: THREE.Material) => m.dispose());
      } else {
        batch.material.dispose();
      }
      batch.removeFromParent();
    }
    this.batches.clear();
    this.entries = [];
    this.unbatched = [];
  }
}

// ---------------------------------------------------------------------------
// Singleton
// ---------------------------------------------------------------------------

let batcher: PropBatcher | null = null;

/** Get or create the global PropBatcher instance. */
export function getPropBatcher(): PropBatcher {
  if (!batcher) {
    batcher = new PropBatcher();
  }
  return batcher;
}

/** Dispose the global PropBatcher. */
export function disposePropBatcher(): void {
  batcher?.dispose();
  batcher = null;
}
